import { CartEvent, CartItem } from '../../events'
import { SubscriptionPromptCookieData } from '../misc/miscellaneous'
import {
    SswSubscriptionPackage,
    SswSubscriptionPrice,
} from './ssw-subscription-types'

export type DecompiledSswCartPayload =
    | ValidDecompiledSswCartPayload
    | ErroredDecompiledSswCartPayload

export interface ValidDecompiledSswCartPayload {
    successful: true
    isOffer: boolean
    // Used for analytics handling, this is the fully rebuilt cart payload which can be sent to GTM
    cartPayload: CartEvent['payload']
    /** The subscription package details, used for various places across the subscription flow, including the offer page and confirmation page  */
    subscriptionPackage: SswSubscriptionPackage
    /** The price object associated with the subscription package, for easy access to the price details across the subscription flow */
    pricePackage: SswSubscriptionPrice
    /** The image path for the subscription package, used for displaying the package image across the subscription flow, this is generated based on a known pattern utilising the package path, so will update accordingly if the package path is updated to reflect offer details */
    imagePath: string | undefined
}
export interface ErroredDecompiledSswCartPayload {
    successful: false
    error: string
    isOffer: boolean
    cartPayload: CartEvent['payload']
    subscriptionPackage: SswSubscriptionPackage
    pricePackage: SswSubscriptionPrice
    imagePath: string | undefined
}

export const decompileSswCartPayload = (
    cartPayload: SubscriptionPromptCookieData,
): DecompiledSswCartPayload | undefined => {
    const cookiePackageInfo = cartPayload?.package_info

    // Subscription Package wasn't found, move on.
    const subPackage: SswSubscriptionPackage | undefined =
        cookiePackageInfo?.package
            ? {
                  ...cookiePackageInfo?.package,
                  type: 'ssw-subscription',
              }
            : undefined
    if (!subPackage) {
        console.warn(
            'Unable to decompile cart payload, missing subscription package information',
        )
        return
    }

    const rateCode = cookiePackageInfo?.item_id
    if (!rateCode) {
        console.warn('Unable to decompile cart payload, missing rate code')
        return
    }

    // Price object wasn't found, move on.
    const priceObject =
        subPackage?.prices?.find((price) => price.rateCode === rateCode) ??
        subPackage?.prices?.[0]
    if (!priceObject) {
        console.warn(
            'Unable to decompile cart payload, missing price information',
        )
        return
    }

    // Check if the default add to cart payload exists, if not we can't rebuild it, move on.
    const defaultAddToCartPayload = cookiePackageInfo?.cart_payload
    if (!defaultAddToCartPayload || !Array.isArray(defaultAddToCartPayload)) {
        return
    }

    const isOffer = cookiePackageInfo?.is_offer === true
    const imagePath = priceObject.packagePath
        ? 'https://subscriber.thewest.com.au/assets/build/images/packages/' +
          priceObject.packagePath.replace('/', '_') +
          '.png'
        : undefined

    // We are not dealing with an offer, so we can use the default payload.
    if (!isOffer) {
        return {
            successful: true,
            isOffer,
            cartPayload: getFullCartPayload(defaultAddToCartPayload),
            subscriptionPackage: subPackage,
            pricePackage: priceObject,
            imagePath,
        }
    }

    const defaultCartItem = defaultAddToCartPayload[0]
    if (!defaultCartItem) {
        return {
            successful: false,
            error: 'Default cart payload is missing item information, therefore cannot update it.',
            isOffer,
            cartPayload: getFullCartPayload(defaultAddToCartPayload),
            subscriptionPackage: subPackage,
            pricePackage: priceObject,
            imagePath,
        }
    }

    // Rebuild the cart item and payload using the package_info values.
    const updatedCartItem: CartItem = {
        ...defaultCartItem,
        // We want to ensure that if any of the critical information is missing from the package_info, we fallback to the default cart item information, as we know this is valid and can be used to ensure the cart payload is still functional, even if we can't reflect all the offer details in it.
        item_name: cookiePackageInfo.item_name ?? defaultCartItem.item_name,
        item_id: cookiePackageInfo.item_id ?? defaultCartItem.item_id,
        promo_price:
            cookiePackageInfo.promo_price ?? defaultCartItem.promo_price,
        discount: cookiePackageInfo.discount ?? defaultCartItem.discount,
        coupon: cookiePackageInfo.coupon ?? defaultCartItem.coupon,
    }

    // Rebuild the price object using the package_info values.
    const updatedPriceObject: SswSubscriptionPrice = {
        ...priceObject,
        rateCode: cookiePackageInfo.item_id ?? priceObject.rateCode,
        promoPrice: cookiePackageInfo.promo_price ?? priceObject.promoPrice,
        weeklyPrice: cookiePackageInfo.price ?? priceObject.weeklyPrice,
        offerCode: cookiePackageInfo.coupon ?? priceObject.offerCode,
    }

    return {
        successful: true,
        isOffer,
        cartPayload: getFullCartPayload([updatedCartItem]),
        subscriptionPackage: subPackage,
        pricePackage: updatedPriceObject,
        imagePath,
    }
}

const getFullCartPayload = (cartItems: CartItem[]): CartEvent['payload'] => {
    return {
        currency: 'AUD',
        value: cartItems.reduce((current, item) => {
            return (
                current +
                (item.promo_price === 0 ? item.price : item.promo_price) *
                    item.quantity
            )
        }, 0),
        items: cartItems,
    }
}
